import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import LinkIcon from '@mui/icons-material/Link';
import { useVolunteer } from '../../contexts/VolunteerContext';
import { formatDate } from '../../utils/helpers';
import { ROUTES } from '../../utils/constants';

type ShiftTypeFilter = 'all' | 'event-linked' | 'standalone';

const VolunteerShiftsPage: React.FC = () => {
  const navigate = useNavigate();
  const { status, shifts, loading, error, fetchStatus, fetchShifts } = useVolunteer();

  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<ShiftTypeFilter>('all');

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (status?.volunteerStatus === 'approved') {
      fetchShifts();
    }
  }, [status?.volunteerStatus, fetchShifts]);

  // Redirect if not approved
  useEffect(() => {
    if (status && status.volunteerStatus !== 'approved') {
      navigate(ROUTES.VOLUNTEERS);
    }
  }, [status, navigate]);

  // Extract unique locations for filter
  const locations = Array.from(new Set(shifts.map((s) => s.location))).sort();

  const filteredShifts = shifts
    .filter((s) => s.isActive)
    .filter((s) => {
      const matchesSearch =
        s.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.location.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLocation = locationFilter === 'all' || s.location === locationFilter;
      const matchesType =
        typeFilter === 'all' ||
        (typeFilter === 'event-linked' && s.eventId) ||
        (typeFilter === 'standalone' && !s.eventId);
      return matchesSearch && matchesLocation && matchesType;
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (loading && !shifts.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Available Shifts
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Browse and sign up for volunteer shifts
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search shifts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 250, flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Location</InputLabel>
          <Select
            value={locationFilter}
            label="Location"
            onChange={(e) => setLocationFilter(e.target.value)}
          >
            <MenuItem value="all">All Locations</MenuItem>
            {locations.map((loc) => (
              <MenuItem key={loc} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value as ShiftTypeFilter)}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="event-linked">Event-linked</MenuItem>
            <MenuItem value="standalone">Standalone</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {filteredShifts.length} shift{filteredShifts.length !== 1 ? 's' : ''}
      </Typography>

      {/* Shifts Grid */}
      {filteredShifts.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No shifts found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filter criteria
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredShifts.map((shift) => {
            const capacityPercent =
              shift.capacity > 0
                ? Math.min((shift.currentVolunteers / shift.capacity) * 100, 100)
                : 0;
            const isFull = shift.currentVolunteers >= shift.capacity;

            return (
              <Grid item xs={12} sm={6} lg={4} key={shift.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: 8,
                      backgroundColor: shift.eventId ? '#9c27b0' : '#00b7ed',
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Chip
                        label={shift.eventId ? 'Event-linked' : 'Standalone'}
                        size="small"
                        sx={{
                          backgroundColor: shift.eventId ? '#9c27b0' : '#00b7ed',
                          color: 'white',
                        }}
                      />
                      {isFull ? (
                        <Chip label="FULL" size="small" color="error" />
                      ) : shift.capacity - shift.currentVolunteers <= 3 ? (
                        <Chip
                          label={`${shift.capacity - shift.currentVolunteers} spots left`}
                          size="small"
                          color="warning"
                        />
                      ) : null}
                    </Box>

                    <Typography variant="h6" component="h2" gutterBottom>
                      {shift.title}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <EventIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(shift.date)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AccessTimeIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {shift.startTime} - {shift.endTime}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {shift.location}
                        {shift.postcode && ` (${shift.postcode})`}
                      </Typography>
                    </Box>

                    {/* Capacity bar */}
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PeopleIcon sx={{ fontSize: 16 }} color="action" />
                          <Typography variant="caption" color="text.secondary">
                            Capacity
                          </Typography>
                        </Box>
                        <Typography variant="caption" fontWeight={500}>
                          {shift.currentVolunteers} / {shift.capacity}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={capacityPercent}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor:
                              capacityPercent >= 90
                                ? '#f44336'
                                : capacityPercent >= 70
                                ? '#ff9800'
                                : '#4caf50',
                          },
                        }}
                      />
                    </Box>

                    {shift.event && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
                        <LinkIcon sx={{ fontSize: 16, color: '#9c27b0' }} />
                        <Typography variant="caption" color="text.secondary">
                          Event: {shift.event.title}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>

                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() =>
                        navigate(ROUTES.VOLUNTEER_SHIFT_DETAIL.replace(':id', shift.id))
                      }
                      disabled={isFull}
                      sx={{
                        backgroundColor: '#00b7ed',
                        '&:hover': { backgroundColor: '#009fcc' },
                      }}
                    >
                      {isFull ? 'Shift Full' : 'View Details'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default VolunteerShiftsPage;
